/**
 * One place that answers "can we reach the server right now?".
 *
 * WHY A PLAIN MODULE AND NOT A PINIA STORE. The three callers that need this
 * answer earliest are `services/httpClient` (an axios interceptor, created
 * before Pinia is installed), `helper/permissionHelper` (a pure synchronous
 * helper the nav calls ~100 times per render) and `plugins/1.router` (a guard
 * that runs before the first component mounts). A store would either import-
 * cycle or force those three to become async. `stores/offline.store.ts` wraps
 * this module for the UI; this module never imports Vue, Pinia or axios.
 *
 * TWO SIGNALS, DELIBERATELY SEPARATE:
 *  - `navigator.onLine` is the browser's guess. It is false only when the
 *    machine knows it has no link, and it lies constantly the other way (a
 *    captive portal, a dead LAN server, Wi-Fi with no route). It is trusted for
 *    "definitely offline" and never for "definitely online".
 *  - Real request outcomes are the truth: a response of any status means the
 *    server answered; a transport failure means it did not.
 *
 * Everything here is deliberately cheap — `isOffline()` sits in hot paths.
 */

export type UnreachableReason = "network" | "timeout" | "browser";

export interface ReachabilitySnapshot {
  /** The browser's own claim. False means definitely no link. */
  browserOnline: boolean;
  /** Our own verdict from real request outcomes. */
  serverReachable: boolean;
  /** Epoch ms of the last response of ANY status, or 0 if none yet. */
  lastOkAt: number;
  /** Epoch ms of the last transport failure, or 0 if none yet. */
  lastFailureAt: number;
  /** Why we last marked the server unreachable. */
  lastFailureReason: UnreachableReason | null;
}

type Listener = (snapshot: ReachabilitySnapshot) => void;

const listeners = new Set<Listener>();

let serverReachable = true;
let lastOkAt = 0;
let lastFailureAt = 0;
let lastFailureReason: UnreachableReason | null = null;

export function isBrowserOnline(): boolean {
  if (typeof navigator === "undefined")
    return true;

  // `navigator.onLine` is `undefined` in some embedded runtimes — absence is
  // not evidence of being offline.
  return navigator.onLine !== false;
}

export function getReachabilitySnapshot(): ReachabilitySnapshot {
  return {
    browserOnline: isBrowserOnline(),
    serverReachable,
    lastOkAt,
    lastFailureAt,
    lastFailureReason,
  };
}

function emit() {
  const snapshot = getReachabilitySnapshot();

  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    }
    catch (error) {
      // A broken subscriber must never break the request that notified it.
      console.warn("Reachability listener failed:", error);
    }
  });
}

/**
 * The server answered — whatever the status. A 401, a 403 and a 500 all prove
 * the connection works, which is exactly the distinction the offline logic
 * needs; only the absence of a response counts as unreachable.
 */
export function markServerReachable(): void {
  lastOkAt = Date.now();

  if (serverReachable)
    return;

  serverReachable = true;
  emit();
}

/** A request failed before any response arrived. */
export function markServerUnreachable(reason: UnreachableReason = "network"): void {
  lastFailureAt = Date.now();
  lastFailureReason = reason;

  if (!serverReachable)
    return;

  serverReachable = false;
  emit();
}

/**
 * True only when we have positive reason to believe the server is reachable.
 * A browser that reports itself offline overrides our own optimism.
 */
export function isServerReachable(): boolean {
  return serverReachable && isBrowserOnline();
}

/**
 * The guard every offline-aware code path asks. Kept as its own function so
 * call sites read as intent ("if we are offline, skip the doomed refresh")
 * rather than as a double negative.
 */
export function isOffline(): boolean {
  return !isServerReachable();
}

export function subscribeReachability(listener: Listener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

let browserEventsBound = false;

/**
 * Bind the browser's own online/offline events. Called once from the
 * network-status plugin; safe to call again.
 *
 * Going offline is trusted immediately. Coming back online is NOT trusted as
 * proof the server is reachable — the next real request (or the heartbeat)
 * decides that — but we do clear our own negative verdict so requests are
 * attempted again instead of being suppressed forever.
 */
export function bindBrowserReachabilityEvents(): void {
  if (browserEventsBound || typeof window === "undefined")
    return;

  browserEventsBound = true;

  window.addEventListener("offline", () => {
    markServerUnreachable("browser");
  });

  window.addEventListener("online", () => {
    serverReachable = true;
    emit();
  });
}
